




























































































































































































































import {
  computed,
  defineComponent,
  reactive,
  ref,
  watch
} from '@vue/composition-api';
import { useQueryClient } from '@tanstack/vue-query';
import { debounce, merge, omit } from 'lodash';
import moment from 'moment';
import { IPaymentPlan, PaymentPlanStateEnum } from '@/models';
import {
  useFetchPaymentPlans,
  useDeletePaymentPlan,
  useCancelPaymentPlan
} from '@/composable/payment-plan';
import { IFetchPaymentPlansParams } from '@/services/payment-plan';
import authMixin from '@/mixins/auth';
import { updateFilters } from '@/utils/filters';

import ALPage from '@/components/core/Page/ALPage.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import PaymentPlanDialog from '@/components/payment-plan/PaymentPlanDialog.vue';
import PaymentPlanStatusDisplay from '@/components/payment-plan/PaymentPlanStatusDisplay.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import PaymentPlanSearchFilter from '@/components/payment-plan/PaymentPlanSearchFilter.vue';
import PaymentPlanMoreOptions from '@/components/payment-plan/PaymentPlanMoreOptions.vue';

export default defineComponent({
  name: 'PaymentPlanListPage',
  mixins: [authMixin],
  components: {
    ALPage,
    ConfirmationDialog,
    ErrorAlert,
    PaymentPlanDialog,
    PaymentPlanStatusDisplay,
    PaymentPlanSearchFilter,
    PaymentPlanMoreOptions
  },
  setup(_, { root }) {
    const filterParams = reactive<IFetchPaymentPlansParams>({
      search: root.$route.query.search as string,
      page: Number(root.$route.query.page ?? 1),
      quote__id: Number(root.$route.query.quote__id as string) || undefined,
      state: root.$route.query.state
        ? Number(root.$route.query.state)
        : undefined
    });

    const isSearchFilterActive = ref(false);
    const extraFiltersApplied = computed<boolean>(() =>
      Object.keys(omit(filterParams, ['page', 'search'])).some(
        key => filterParams[key as keyof IFetchPaymentPlansParams] != undefined
      )
    );

    //We use a separate ref for the search model in order to not update the queryKey once the search is updated
    const currentSearch = ref<string>(filterParams.search ?? '');

    const headers = [
      {
        text: 'Customer #',
        value: 'customer_number',
        sortable: false
      },
      { text: 'Customer', value: 'customer_name', sortable: false },
      { text: 'Proposal #', value: 'quote', sortable: false },
      { text: 'CSI Order #', value: 'csi_order_number', sortable: false },
      { text: 'PO #', value: 'po_number', sortable: false },
      { text: 'Ex-Factory', value: 'ex_factory_date', sortable: false },
      { text: 'Sign Off', value: 'sign_off_date', sortable: false },
      { text: 'Next Invoice', value: 'next_payment_date', sortable: false },
      { text: 'Status', value: 'state', align: 'center', sortable: false },
      { text: '', value: 'actions', sortable: false, width: 100 }
    ];

    const dialog = ref(false);
    const selectedPaymentPlan = ref<IPaymentPlan | null>(null);

    function openPaymentPlanDialog(paymentPlan?: IPaymentPlan) {
      dialog.value = true;
      selectedPaymentPlan.value = paymentPlan ?? null;
    }

    const {
      isLoading: deleteInProgress,
      error: deleteError,
      mutate: deletePaymentPlan
    } = useDeletePaymentPlan();

    const deleteDialog = ref(false);
    function openDeletePaymentPlanDialog(paymentPlan: IPaymentPlan) {
      selectedPaymentPlan.value = paymentPlan;
      deleteDialog.value = true;
    }

    function closeDialog() {
      deleteDialog.value = false;
    }

    function removePaymentPlan(vehicle: IPaymentPlan) {
      deletePaymentPlan(vehicle.id!, {
        onSuccess: () => {
          closeDialog();
        }
      });
    }

    function clearAllErrors() {
      updateReset();
      fetchError.value = null;
      setStateError.value = null;
    }

    const {
      paymentPlans,
      error: fetchError,
      isLoading: isFetchLoading,
      count,
      queryKey
    } = useFetchPaymentPlans(filterParams);

    const error = computed(() => fetchError.value || setStateError.value);
    const isLoading = computed<boolean>(
      () => isFetchLoading.value || isSetStateLoading.value
    );

    // CORE
    const queryClient = useQueryClient();

    const debounceFilterPaymentPlans = debounce(function(
      filter: IFetchPaymentPlansParams
    ) {
      // mutates filters
      merge(filterParams, filter);
      queryClient.invalidateQueries({ queryKey: queryKey.value });
    },
    700);

    watch(
      filterParams,
      newParams => {
        /* this will update the URL query based on param values. */
        root.$router
          .replace({
            name: root.$route.name!,
            query: newParams as Record<string, any>
          })
          .catch((e: Error) => e); //catch navigation duplication error
      },
      { immediate: true }
    );

    function onUpdatePage(page: number) {
      filterParams.page = page;
    }

    function goToDetailPage(paymentPlan: IPaymentPlan) {
      root.$router.push({
        name: `payment-plan-detail`,
        params: {
          id: String(paymentPlan.id)
        }
      });
    }

    const {
      isLoading: isSetStateLoading,
      error: setStateError,
      mutate: setCancellationState,
      reset: updateReset
    } = useCancelPaymentPlan();

    function cancelPaymentPlan(id: number) {
      clearAllErrors();
      setCancellationState(id);
    }

    return {
      PaymentPlanStateEnum,
      deleteInProgress,
      deleteDialog,
      deleteError,
      selectedPaymentPlan,
      isSearchFilterActive,
      extraFiltersApplied,
      moment,
      dialog,
      filterParams,
      currentSearch,
      paymentPlans,
      isLoading,
      headers,
      error,
      count,
      onUpdatePage,
      updateFilters,
      openPaymentPlanDialog,
      clearAllErrors,
      openDeletePaymentPlanDialog,
      goToDetailPage,
      closeDialog,
      removePaymentPlan,
      cancelPaymentPlan,
      debounceFilterPaymentPlans
    };
  }
});
